import Axios, { AxiosResponse } from 'axios';
import Product from '../models/Product';
import ProductPlan from '../models/ProductPlan';
import {
  dedupeObjectArray,
  findObjectInArray,
  sortObjectListByAccessor,
} from '../lib/utils';
import Discounts from '../models/Discounts';
import paymentAPI from 'src/pages/deprecated/payment/api';

interface AddonPurchaseResponse {
  data: boolean;
  isSuccess: boolean;
  requiresPaymentCompletion: boolean;
}

export interface CustomerAcceptance {
  customer_acceptance: {
    type: 'online';
    online: {
      ip_address: string;
      user_agent: string;
    };
  };
}

const FALLBACK_IP_ADDRESS = '127.0.0.0';

// Cached for the lifetime of the session so the IP is only fetched once,
// no matter how many payment flows the user goes through.
let customerAcceptancePromise: Promise<CustomerAcceptance> | null = null;

const inclusions: { [planId: number]: string[] } = {
  1: [
    'Daily Targets',
    'Customized Meal Plan',
    'Calorie tracking tool',
    'Weekly Check-in',
    'Chat support',
  ],
  2: ['Calorie tracking tool', 'Weekly Check-in', 'Chat support'],
};

export default class ProductService {
  public plans: ProductPlan[] = [];
  public products: Product[] = [];
  public activeProduct: Partial<Product> = {};

  public getProductsForPlanId(planId: number): Product[] {
    return this.products.filter((product: Product) =>
      findObjectInArray(planId, product.productPlanModules, 'id')
    );
  }

  public async getConsumableProducts(): Promise<Product[]> {
    const response: AxiosResponse = await paymentAPI.get(
      `/getConsumableProducts`
    );
    const { data } = response;

    this.activeProduct = data;

    return data;
  }

  public getPlanById(planId: number): ProductPlan | undefined {
    return this.plans.find((plan: ProductPlan) => plan.id === planId);
  }

  public static async applyPromoCode(
    code: string,
    productIds: number[] | string[],
    userId: number
  ): Promise<Discounts | undefined> {
    let discountedProducts: Discounts | undefined;
    try {
      const response: AxiosResponse = await Axios.post(
        '/apply-promo-code-codes',
        {
          code,
          productIds,
          userId,
        }
      );
      discountedProducts = response.data as Discounts;
    } catch (error) {
      console.error('Could not apply promo code', error);
    }

    return discountedProducts;
  }

  public async getProductOptions(
    restrictedToIds: number[],
    includeSubscription = false
  ): Promise<ProductPlan[]> {
    try {
      const response: AxiosResponse = await paymentAPI.get('/product-options');
      const { data } = response;

      this.products = data.map((product: any) => {
        return product as Product;
      });

      if (!includeSubscription) {
        this.products = this.products.filter(
          product => product.duration !== 1 && product.interval === 'WEEKS'
        );
      }

      const plans: ProductPlan[] = [];

      data.forEach((product: any) => {
        const assocPlan: ProductPlan = product.productPlanModules.find(
          (plan: any) => restrictedToIds.includes(plan.id)
        );

        if (assocPlan) {
          assocPlan.inclusions = inclusions[assocPlan.id];
          assocPlan.descriptionShown = false;
          plans.push(assocPlan);
        }
      });
      this.plans = sortObjectListByAccessor(
        dedupeObjectArray(plans, 'id'),
        'id'
      );
    } catch (error) {
      console.error('There was an error getting product options');
    }
    return this.plans;
  }

  public async getActiveProductsForUserId(userId: number): Promise<Product> {
    const response: AxiosResponse = await paymentAPI.get(
      `/getActiveClientProducts/${userId}`
    );
    const { data } = response;

    this.activeProduct = data;

    return data;
  }

  public static async removeNutritionCoachingAddon(params: {
    userId: number;
    email: string;
  }): Promise<void> {
    try {
      // First, get or create the Stripe customer to obtain customerID
      const customerResponse: AxiosResponse = await paymentAPI.post(
        'stripe/customer/create',
        {
          email: params.email,
          userID: params.userId,
        }
      );

      const customerID = customerResponse.data.customer?.id;

      if (!customerID) {
        throw new Error('Failed to retrieve customer ID');
      }

      // Now remove the nutrition coaching addon
      await paymentAPI.post('stripe/invoice/remove-nutrition-coaching-addon', {
        customerID,
      });
    } catch (error) {
      console.error('Failed to remove nutrition coaching addon:', error);
      throw error;
    }
  }

  public static async purchaseNutritionCoachingAddon(params: {
    userId: number;
    email: string;
    paymentMethodId: string;
    priceIds: string[];
    customerId?: string;
    isAfterPay?: boolean;
    isCardPayment?: boolean;
    paymentIntentId?: string;
    claimGift?: boolean;
  }): Promise<AxiosResponse<AddonPurchaseResponse>> {
    try {
      // Use provided customerId or create/get customer
      let customerID: string;

      if (params.customerId) {
        customerID = params.customerId;
      } else {
        // Get or create the Stripe customer to obtain customerID
        const customerResponse: AxiosResponse = await paymentAPI.post(
          'stripe/customer/create',
          {
            email: params.email,
            userID: params.userId,
          }
        );

        customerID = customerResponse.data.customer?.id;

        if (!customerID) {
          throw new Error('Failed to retrieve customer ID');
        }
      }

      const payload = {
        customerID,
        paymentMethodID: params.paymentMethodId,
        priceIDs: params.priceIds,
        userID: params.userId,
        isAfterPay: params.isAfterPay,
        isCardPayment: params.isCardPayment,
        paymentIntentId: params.paymentIntentId,
        ...(params.claimGift ? { claimGift: true } : {}),
      };

      // Now purchase the nutrition coaching addon
      const response: AxiosResponse<AddonPurchaseResponse> =
        await paymentAPI.post('stripe/invoice/purchase-addons', payload);
      return response;
    } catch (error) {
      console.error('Failed to purchase nutrition coaching addon:', error);
      throw error;
    }
  }

  public static async initiateAfterpayPaymentNutritionCoach(params: {
    userId: number;
    email: string;
    priceId: string;
  }): Promise<{
    clientSecret: string;
    requiresPaymentCompletion: boolean;
  }> {
    try {
      // First, get or create the Stripe customer to obtain customerID
      const customerResponse: AxiosResponse = await paymentAPI.post(
        'stripe/customer/create',
        {
          email: params.email,
          userID: params.userId,
        }
      );

      const customerID = customerResponse.data.customer?.id;

      if (!customerID) {
        throw new Error('Failed to retrieve customer ID');
      }

      const purchaseResponse = await paymentAPI.post(
        'afterpay/create-subscription',
        {
          priceId: params.priceId,
          customerId: customerID,
        }
      );

      if (purchaseResponse.data.clientSecret) {
        return {
          clientSecret: purchaseResponse.data.clientSecret,
          requiresPaymentCompletion: true,
        };
      } else {
        throw new Error(purchaseResponse.data.message || 'Purchase failed');
      }
    } catch (error) {
      console.error('Failed to initiate Afterpay payment:', error);
      throw error;
    }
  }

  public static async initiateAfterpayPayment(params: {
    userId: number;
    email: string;
    priceId: string;
    isChallengePackage?: boolean;
  }): Promise<{
    clientSecret: string;
    requiresPaymentCompletion: boolean;
  }> {
    try {
      // First, get or create the Stripe customer to obtain customerID
      const customerResponse: AxiosResponse = await paymentAPI.post(
        'stripe/customer/create',
        {
          email: params.email,
          userID: params.userId,
        }
      );

      const customerID = customerResponse.data.customer?.id;

      if (!customerID) {
        throw new Error('Failed to retrieve customer ID');
      }

      // Now initiate the Afterpay payment
      const invoicePayload = {
        paymentMethodID: undefined, // No payment method for Afterpay
        customerID,
        priceIDs: [params.priceId],
        promoID: null,
        userID: params.userId,
        isChallenge: false,
        isChallengePackage: params.isChallengePackage || false,
        isAfterPay: true,
      };

      const purchaseResponse = await paymentAPI.post(
        'stripe/invoice/purchase',
        invoicePayload
      );

      if (purchaseResponse.data.isSuccess) {
        if (purchaseResponse.data.requiresPaymentCompletion) {
          return {
            clientSecret: purchaseResponse.data.clientSecret,
            requiresPaymentCompletion: true,
          };
        } else {
          return {
            clientSecret: '',
            requiresPaymentCompletion: false,
          };
        }
      } else {
        throw new Error(purchaseResponse.data.message || 'Purchase failed');
      }
    } catch (error) {
      console.error('Failed to initiate Afterpay payment:', error);
      throw error;
    }
  }

  /**
   * Stripe mandate data for Afterpay/Clearpay confirmations. The IP address is
   * resolved by the backend (the browser cannot read its own public IP) and
   * memoized for the session; the user agent comes from the browser itself.
   */
  public static async getCustomerAcceptance(): Promise<CustomerAcceptance> {
    if (!customerAcceptancePromise) {
      customerAcceptancePromise = paymentAPI
        .get('stripe/client-ip')
        .then((response: AxiosResponse) => ({
          customer_acceptance: {
            type: 'online' as const,
            online: {
              ip_address:
                response.data?.data?.ip_address || FALLBACK_IP_ADDRESS,
              user_agent: navigator.userAgent,
            },
          },
        }))
        .catch(error => {
          console.error('Failed to resolve client IP for mandate data:', error);
          // Allow a retry on the next payment attempt.
          customerAcceptancePromise = null;
          return {
            customer_acceptance: {
              type: 'online' as const,
              online: {
                ip_address: FALLBACK_IP_ADDRESS,
                user_agent: navigator.userAgent,
              },
            },
          };
        });
    }

    return customerAcceptancePromise;
  }

  public static async completeAfterpayPayment(params: {
    paymentIntentId: string;
    userId: number;
  }): Promise<boolean> {
    try {
      const response = await paymentAPI.post(
        '/afterpay/complete-addon-purchase',
        {
          paymentIntentId: params.paymentIntentId,
          userId: params.userId,
        }
      );

      return response.data.data === true;
    } catch (error) {
      console.error('Failed to complete Afterpay payment:', error);
      throw error;
    }
  }
}
